import { useState, useCallback } from 'react';
import { paymentDocumentsApi } from '../../api/expertDashboardApis/paymentDocumentsApi';

// 🟢 Added Interface for perfect autocomplete in your UI
export interface PaymentDocumentData {
  expert_name?: string;
  expert_number?: string;
  pan_number: string;
  pan_card_url: string;
  aadhaar_number: string;
  aadhaar_front_url: string;
  aadhaar_back_url: string;
  
  bank_account_name: string;
  bank_account_type: string; // 🟢 ADD THIS EXACT LINE
  bank_account_number: string;
  bank_ifsc_code: string;
  bank_proof_url: string;
  
  verification_status?: 'pending' | 'approved' | 'rejected';
  admin_remarks?: string;
}

export const usePaymentDocuments = () => {
  const [documents, setDocuments] = useState<PaymentDocumentData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [uploading, setUploading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchDocuments = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await paymentDocumentsApi.getPaymentDocuments();
      if (res.success && res.data) {
        setDocuments(res.data);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch documents');
    } finally {
      setLoading(false);
    }
  }, []);

  // Handles Upload + 5MB Size Validation
  const uploadFile = async (file: File): Promise<string | null> => {
    // File Size Check (5MB = 5 * 1024 * 1024 bytes)
    const MAX_FILE_SIZE = 5 * 1024 * 1024;
    if (file.size > MAX_FILE_SIZE) {
      setError('File is too large. Maximum size allowed is 5MB.');
      return null;
    }

    setUploading(true);
    setError(null);
    try {
      const res = await paymentDocumentsApi.uploadDocumentFile(file);
      if (res.success) {
        return res.url; 
      }
      throw new Error('Upload failed');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to upload file');
      return null;
    } finally {
      setUploading(false);
    }
  };

  const submitDocuments = async (formData: Partial<PaymentDocumentData>) => {
    setLoading(true);
    setError(null);
    try {
      const res = await paymentDocumentsApi.savePaymentDocuments(formData);
      if (res.success) {
        setDocuments(res.data);
        return true;
      }
      return false;
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to submit documents');
      return false;
    } finally {
      setLoading(false);
    }
  };

  return {
    documents,
    loading,
    uploading,
    error,
    fetchDocuments,
    uploadFile,
    submitDocuments,
  };
};